import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@domains/auth';
import { RemoteConfigProvider, useRemoteConfig } from './context/RemoteConfigContext';
import Navbar from './components/Navbar';
import { logPageView } from './services/analytics';
import { PopupProvider } from '@shared/ui/Popup';

const Login = lazy(() => import('@domains/auth/pages/Login'));
const Register = lazy(() => import('@domains/auth/pages/Register'));
const ForgotPassword = lazy(() => import('@domains/auth/pages/ForgotPassword'));
const AuthDesktop = lazy(() => import('@domains/auth/pages/AuthDesktop'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const SharedWheel = lazy(() => import('@domains/wheels/pages/SharedWheel'));
const DemoWheel = lazy(() => import('@domains/wheels/pages/DemoWheel'));
const ValueWheelsPage = lazy(() => import('@domains/wheels/pages/ValueWheelsPage'));
const SharedTest = lazy(() => import('@domains/tests/pages/SharedTest'));
const TestsPage = lazy(() => import('@domains/tests/pages/TestsPage'));
const ClientsPage = lazy(() => import('@domains/clients/pages/ClientsPage'));
const ClientDashboardPage = lazy(() => import('@domains/clients/pages/ClientDashboardPage'));
const ClientMeetingsPage = lazy(() => import('@domains/clients/pages/ClientMeetingsPage'));
const ClientPrintNotesPage = lazy(() => import('@domains/clients/pages/ClientPrintNotesPage'));
const ClientPortalPage = lazy(() => import('@domains/clients/pages/ClientPortalPage'));
const SharedGallery = lazy(() => import('@domains/galleries/pages/SharedGallery'));
const GalleriesPage = lazy(() => import('@domains/galleries/pages/GalleriesPage'));
const BoardsPage = lazy(() => import('@domains/boards/pages/BoardsPage'));
const SharedBoard = lazy(() => import('@domains/boards/pages/SharedBoard'));
const SharedDocument = lazy(() => import('@domains/documents/pages/SharedDocument'));
const CalendarPage = lazy(() => import('@domains/calendar/pages/CalendarPage'));
const PaymentsPage = lazy(() => import('@domains/payments/pages/PaymentsPage'));
const ProfilePage = lazy(() => import('@domains/profile/pages/ProfilePage'));
const TasksPage = lazy(() => import('@domains/tasks/pages/TasksPage'));
const JournalPage = lazy(() => import('@domains/journal/pages/JournalPage'));
const JournalEntryPage = lazy(() => import('@domains/journal/pages/JournalEntryPage'));
const JournalPrintPage = lazy(() => import('@domains/journal/pages/JournalPrintPage'));

// Komponent sprawdzający uprawnienia (Protected Route)
interface ProtectedRouteProps {
  children: React.ReactNode;
}

function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { currentUser } = useAuth();
  return currentUser ? <>{children}</> : <Navigate to="/" replace />;
}

// Komponent sprawdzający aktywną subskrypcję (Paid Route)
interface PaidRouteProps {
  children: React.ReactNode;
}

function PaidRoute({ children }: PaidRouteProps) {
  const { subscriptionStatus } = useAuth();
  const { requireSubscription, enablePayment } = useRemoteConfig();
  
  if (requireSubscription && subscriptionStatus !== 'active') {
    if (enablePayment) {
      return <Navigate to="/dashboard/payments" replace />;
    } else {
      return <Navigate to="/dashboard" replace />;
    }
  }
  
  return <>{children}</>;
}


// Estetyczny wskaźnik ładowania podczas pobierania komponentów (Suspense)
function PageLoader() {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      height: '100%',
      flex: 1,
      minHeight: '400px',
      color: 'var(--text-muted)',
      gap: '16px',
      animation: 'fadeIn 0.3s ease-out'
    }}>
      <div style={{
        width: '40px',
        height: '40px',
        border: '3px solid rgba(255, 255, 255, 0.05)',
        borderTop: '3px solid var(--accent-primary)',
        borderRadius: '50%',
        animation: 'loader-spin 1s linear infinite'
      }} />
      <span style={{ fontSize: '0.9rem', letterSpacing: '0.05em' }}>Ładowanie...</span>
      <style>{`
        @keyframes loader-spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

// Inner component, which handles conditional layout based on URL
function AppContent() {
  const location = useLocation();
  const { enablePayment, enableNewPalette } = useRemoteConfig();
  const isPrintPage = location.pathname.includes('/print-notes') || location.pathname.includes('/journal/print');
  const isDashboard = location.pathname.startsWith('/dashboard') && !isPrintPage;
  const isShared = location.pathname.startsWith('/share') || 
                   location.pathname.startsWith('/s/') ||
                   location.pathname.startsWith('/t/') ||
                   location.pathname.startsWith('/share-test') ||
                   location.pathname.startsWith('/g/') ||
                   location.pathname.startsWith('/share-gallery') ||
                   location.pathname.startsWith('/b/') ||
                   location.pathname.startsWith('/share-board') ||
                   location.pathname.startsWith('/portal') ||
                   location.pathname.startsWith('/c/');
  const isLoginPage = location.pathname === '/' || location.pathname === '/login';
  const isAuthDesktop = location.pathname === '/auth-desktop';
  const showNavbar = !isDashboard && !isShared && !isPrintPage && !isLoginPage && !isAuthDesktop;

  React.useEffect(() => {
    if (enableNewPalette) {
      document.body.classList.add('new-palette-theme');
    } else {
      document.body.classList.remove('new-palette-theme');
    }
  }, [enableNewPalette]);

  React.useEffect(() => {
    logPageView(location.pathname);
  }, [location.pathname]);

  return (
    <div className="app-container">
      {showNavbar && <Navbar />}
      <main 
        className={isDashboard ? "dashboard-layout-container" : (isShared || isPrintPage) ? "shared-layout-container" : "main-content"}
        style={isLoginPage ? { paddingTop: '40px' } : undefined}
      >
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Publiczne ścieżki */}
            <Route path="/" element={<Login />} />
            <Route path="/login" element={<Navigate to="/" replace />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/auth-desktop" element={<AuthDesktop />} />
            <Route path="/share/:uid/:wheelId" element={<SharedWheel />} />
            <Route path="/s/:shortId" element={<SharedWheel />} />
            <Route path="/share-test/:uid/:testId" element={<SharedTest />} />
            <Route path="/t/:shortId" element={<SharedTest />} />
            <Route path="/share-gallery/:uid/:galleryId" element={<SharedGallery />} />
            <Route path="/g/:shortId" element={<SharedGallery />} />
            <Route path="/share-board/:uid/:boardId" element={<SharedBoard />} />
            <Route path="/b/:shortId" element={<SharedBoard />} />
            <Route path="/share-document/:uid/:documentId" element={<SharedDocument />} />
            <Route path="/d/:shortId" element={<SharedDocument />} />
            <Route path="/doc/:shortId" element={<SharedDocument />} />
            <Route path="/portal/:clientId" element={<ClientPortalPage />} />
            <Route path="/c/:clientId" element={<ClientPortalPage />} />
            <Route path="/demo" element={<DemoWheel />} />
            
            {/* Chronione ścieżki */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }>
              <Route index element={<Navigate to="wheels" replace />} />
              <Route path="calendar" element={<CalendarPage />} />
              <Route path="wheels" element={<ValueWheelsPage />} />
              <Route path="tests" element={<TestsPage />} />
              <Route path="clients" element={<ClientsPage />} />
              <Route path="client/:clientId" element={
                <PaidRoute>
                  <ClientDashboardPage />
                </PaidRoute>
              } />
              <Route path="client/:clientId/meetings" element={
                <PaidRoute>
                  <ClientMeetingsPage />
                </PaidRoute>
              } />
              <Route path="galleries" element={<GalleriesPage />} />
              <Route path="boards" element={<BoardsPage />} />
              <Route path="payments" element={
                enablePayment ? <PaymentsPage /> : <Navigate to="/dashboard" replace />
              } />
              <Route path="profile" element={<ProfilePage />} />
              <Route path="tasks" element={<TasksPage />} />
              <Route path="journal" element={<JournalPage />} />
              <Route path="journal/:entryId" element={<JournalEntryPage />} />
            </Route>

            <Route path="/dashboard/client/:clientId/print-notes" element={
              <ProtectedRoute>
                <PaidRoute>
                  <ClientPrintNotesPage />
                </PaidRoute>
              </ProtectedRoute>
            } />

            <Route path="/dashboard/journal/print" element={
              <ProtectedRoute>
                <JournalPrintPage />
              </ProtectedRoute>
            } />

            {/* Obsługa nieznanych ścieżek */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </main>
    </div>
  );
}

function App() {
  return (
    <Router>
      <RemoteConfigProvider>
        <AuthProvider>
          <PopupProvider>
            <AppContent />
          </PopupProvider>
        </AuthProvider>
      </RemoteConfigProvider>
    </Router>
  );
}

export default App;
